import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AgentEditForm from "../../../../../components/dashboardcomponents/People/Agents/AgentEdit/AgentEditForm";
import { AgentEditPageSkeleton } from "../../../../../components/dashboardcomponents/People/Agents/AgentLoadingSkeletons";
import { AGENT_EDIT_SAVE, AGENT_EDIT_CANCEL } from "../../../../../components/dashboardcomponents/People/Agents/agentEvents";
import { buildAgentDetailView } from "../../../../../components/dashboardcomponents/People/Agents/AgentDetail/agentDetailHelpers";
import {
  fetchAgentById,
  updateAgent,
} from "../../../../../store/features/agentsApi";
import useCustomerFormOptions from "../../../../../hooks/useCustomerFormOptions";
import usePeoplePermissions from "../../../../../hooks/usePeoplePermissions";
import { buildAgentUpdatePayload, isCoUserApiSuccess } from "../../../../../utils/agentMappers";
import { isAdminRole } from "../../../../../utils/peoplePermissions";
import {
  firstValidationError,
  hasValidationErrors,
  validateAgentForm,
} from "../../../../../components/dashboardcomponents/People/peopleFormValidation";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

/** Prefill Status select from co-user API `status` (mapped on the detail view). */
function presenceFromView(view) {
  const presence = view?.presence;
  if (
    presence === "accepting" ||
    presence === "notAccepting" ||
    presence === "offline" ||
    presence === "invitationSent" ||
    presence === "suspended"
  ) {
    return presence;
  }
  return "accepting";
}

function buildInitialForm(view) {
  return {
    name: view.name,
    email: view.email,
    jobTitle: view.jobTitle === "—" ? "" : view.jobTitle,
    roleName: view.roleName,
    presence: presenceFromView(view),
    chatLimit:
      view.chatLimit != null && view.chatLimit !== "—" ? String(view.chatLimit) : "5",
    timezone:
      view.timezone && view.timezone !== "—"
        ? view.timezone
        : "America/Los_Angeles",
    groupIds: [...(view.groupIds || [])],
    groupSelect: "",
    teamIds: [...(view.teamIds || [])],
    teamSelect: "",
    kbRole: view.kbRole || "",
    avatar: view.avatar,
    avatarPreview: "",
    avatarFile: null,
  };
}

export default function AgentEditPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading } = useSelector((state) => state.peopleAgents);
  const currentUserId = useSelector(
    (state) =>
      state.loginToken?.response?.user?.id ||
      state.loginToken?.response?.user?._id ||
      state.loginTokenCoUser?.response?.user?.id ||
      state.loginTokenCoUser?.response?.user?._id
  );
  const { groupOptions, teamOptions, loading: optionsLoading } = useCustomerFormOptions();
  const perms = usePeoplePermissions();
  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agentId) dispatch(fetchAgentById({ agentId }));
  }, [agentId, dispatch]);

  useEffect(() => {
    if (!detail) return;
    const detailId = detail?.id || detail?._id;
    if (String(detailId) !== String(agentId)) return;
    const view = buildAgentDetailView(detail);
    if (!view) return;
    const nextForm = buildInitialForm(view);
    setForm(nextForm);
    setInitialForm(nextForm);
  }, [detail, agentId]);

  useEffect(() => {
    if (!detail || detailLoading) return;
    const view = buildAgentDetailView(detail);
    if (!view) return;
    if (!perms.agents.forAgent(agentId, view.roleName).canEdit) {
      toast.error("You don't have permission to edit this agent");
      navigate(`/people/agents/${agentId}`);
    }
  }, [detail, detailLoading, agentId, perms, navigate]);

  const handleSave = useCallback(async () => {
    if (!form || saving) return;
    const errors = validateAgentForm(form);
    if (hasValidationErrors(errors)) {
      toast.error(firstValidationError(errors));
      return;
    }

    const editPerms = perms.agents.forAgent(agentId, form.roleName);
    if (!editPerms.canEdit) {
      toast.error("You don't have permission to edit this agent");
      return;
    }

    setSaving(true);
    try {
      const isSelfEdit =
        currentUserId != null &&
        agentId != null &&
        String(currentUserId) === String(agentId);
      const body = buildAgentUpdatePayload(form, {
        initialForm,
        includeRole: !isSelfEdit,
      });
      if (Object.keys(body).length === 0) {
        toast.info("No changes to save");
        return;
      }
      const result = await dispatch(updateAgent({ agentId, body }));
      if (updateAgent.rejected.match(result) || !isCoUserApiSuccess(result.payload)) {
        toast.error(apiMessage(result.payload, "Failed to update agent"));
        return;
      }

      toast.success(apiMessage(result.payload, "Agent updated"));
      navigate(`/people/agents/${agentId}`);
    } finally {
      setSaving(false);
    }
  }, [form, initialForm, saving, dispatch, agentId, navigate, perms, currentUserId]);

  useEffect(() => {
    const onSave = () => handleSave();
    const onCancel = () => navigate(`/people/agents/${agentId}`);
    window.addEventListener(AGENT_EDIT_SAVE, onSave);
    window.addEventListener(AGENT_EDIT_CANCEL, onCancel);
    return () => {
      window.removeEventListener(AGENT_EDIT_SAVE, onSave);
      window.removeEventListener(AGENT_EDIT_CANCEL, onCancel);
    };
  }, [handleSave, navigate, agentId]);

  if ((detailLoading && !detail) || (detail && !form)) return <AgentEditPageSkeleton />;

  const isSelfEdit =
    currentUserId != null &&
    agentId != null &&
    String(currentUserId) === String(agentId);
  const roleDisabled = isSelfEdit || !isAdminRole(perms.roleName);

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>Agent not found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: 3,
        py: 3,
        bgcolor: "#FFF",
      }}
    >
      <AgentEditForm
        form={form}
        onChange={setForm}
        groupOptions={groupOptions}
        teamOptions={teamOptions}
        optionsLoading={optionsLoading}
        roleDisabled={roleDisabled}
      />
    </Box>
  );
}
